/**
 * Lecture du GS1 Barcode Syntax Dictionary (ADR-020 §3) : la table des Application Identifiers
 * vient de la donnée officielle versionnée dans le dépôt (`data/gs1-syntax-dictionary.txt`,
 * Apache-2.0), jamais d'une table recopiée à la main. Ce module ne lit aucun fichier : il analyse
 * un texte ; le script `scripts/generate-ai-table.ts` en tire la table embarquée, et un test
 * vérifie que la table embarquée est exactement le résultat de cette analyse.
 *
 * Syntaxe d'une ligne : `AIs [Flags] Specification [Attributes...] [# Title]`.
 */
const COMPONENT = /^(\[)?([NXYZ])(\d{1,3}|\.\.\d{1,3})(\])?((?:,[a-z0-9]+)*)$/;
const AI_RANGE = /^(\d{2,4})(?:-(\d{2,4}))?$/;
const FLAGS = /^[*!?"$%&'()+,\-./:;<=>@[\\\]^_`{|}~]+$/;
/** Analyse le texte du dictionnaire ; une ligne mal formée est une erreur, jamais ignorée. */
export function parseSyntaxDictionary(text) {
    const entries = [];
    const lines = text.split(/\r?\n/);
    lines.forEach((raw, index) => {
        const line = raw.trim();
        if (line === '' || line.startsWith('#'))
            return;
        const hash = line.indexOf('#');
        const title = hash === -1 ? '' : line.slice(hash + 1).trim();
        const tokens = (hash === -1 ? line : line.slice(0, hash)).trim().split(/\s+/);
        const fail = (message) => {
            throw new Error(`Dictionnaire GS1, ligne ${index + 1} : ${message} (« ${line} »).`);
        };
        const range = AI_RANGE.exec(tokens.shift() ?? '');
        if (range === null)
            return fail('AI ou plage d’AI illisible');
        let flags = '';
        if (tokens[0] !== undefined && FLAGS.test(tokens[0]))
            flags = tokens.shift();
        const components = [];
        while (tokens[0] !== undefined && COMPONENT.test(tokens[0])) {
            const match = COMPONENT.exec(tokens.shift());
            const optional = match[1] === '[';
            if (optional !== (match[4] === ']'))
                fail('crochets déséquilibrés');
            const length = match[3];
            const max = Number(length.replace('..', ''));
            components.push({ charset: match[2], min: length.startsWith('..') ? 1 : max, max, optional, linters: match[5] === '' ? [] : match[5].slice(1).split(',') });
        }
        if (components.length === 0)
            fail('spécification absente');
        const req = [];
        const ex = [];
        let dlpkey;
        for (const token of tokens) {
            const [key, value] = token.includes('=') ? [token.slice(0, token.indexOf('=')), token.slice(token.indexOf('=') + 1)] : [token, undefined];
            if (key === 'req' && value !== undefined)
                req.push(value.split(',').map((alternative) => alternative.split('+')));
            else if (key === 'ex' && value !== undefined)
                ex.push(...value.split(','));
            else if (key === 'dlpkey')
                dlpkey = value === undefined ? [[]] : value.split('|').map((sequence) => sequence.split(','));
            else
                fail(`attribut inconnu « ${token} »`);
        }
        const first = Number(range[1]);
        const last = range[2] === undefined ? first : Number(range[2]);
        if (range[2] !== undefined && range[1].length !== range[2].length)
            fail('bornes de plage de longueurs différentes');
        for (let value = first; value <= last; value += 1) {
            const entry = {
                ai: String(value).padStart(range[1].length, '0'),
                fixed_length: flags.includes('*'),
                dl_attribute: flags.includes('?'),
                components: components.map((component) => ({ ...component, linters: [...component.linters] })),
                req: req.map((rule) => rule.map((alternative) => [...alternative])),
                ex: [...ex],
                title,
            };
            if (dlpkey !== undefined)
                entry.dlpkey = dlpkey.map((sequence) => [...sequence]);
            entries.push(entry);
        }
    });
    return entries;
}
/** Vrai si un AI correspond à un motif du dictionnaire (`35nn`, `310n`, `01`). */
export function aiMatchesPattern(ai, pattern) {
    if (ai.length !== pattern.length)
        return false;
    for (let index = 0; index < pattern.length; index += 1) {
        const expected = pattern[index];
        if (expected === 'n' ? !(ai[index] >= '0' && ai[index] <= '9') : expected !== ai[index])
            return false;
    }
    return true;
}
//# sourceMappingURL=syntax-dictionary.js.map